/**
 * EmailComposeModal - Modal for composing and sending document emails
 * PROMPT 41: Financial Communications
 * PROMPT 66: Client Contacts Integration - Auto-populate recipients based on document type
 */
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { Mail, X, Plus, Loader2, FileText, AlertTriangle, Users, Star } from "lucide-react";
import { getDefaultEmailTemplate, getClientContacts, getClientPortalUrl, extractErrorMessage } from "../../lib/api";
// EVENTSFLO-QUOTE-INVOICE-SEND-RECIPIENT-CONFIRMATION-V1
import { parseRecipients, isValidEmail } from "../../lib/recipientParser";
import { toast } from "sonner";

/**
 * Email variable replacer
 * EVENTSFLO-QUOTE-INVOICE-EMAIL-BODY-TEMPLATES-SETTINGS-V1
 * Supports BOTH `{{key}}` (legacy, generated by seed-defaults) and
 * `{key}` (new-style, per V1 spec). Unknown placeholders are left as-is
 * so the operator can spot them; empty substitutions are cleaned up so
 * we never render "None", "null", "undefined", or awkward " for ." lines.
 */
const replaceVariables = (text, variables) => {
  if (!text) return "";
  // First, substitute {{key}} and {key} against the variables map.
  const substituted = text.replace(
    /\{\{(\w+)\}\}|\{(\w+)\}/g,
    (match, k1, k2) => {
      const key = k1 || k2;
      const raw = variables[key];
      if (raw === undefined) return match; // unknown → leave verbatim
      const val = raw === null ? "" : String(raw);
      // Guard against JS-y null/undefined/None literals leaking into
      // the rendered body if callers accidentally pass them as strings.
      if (["null", "undefined", "none"].includes(val.toLowerCase().trim())) {
        return "";
      }
      return val;
    },
  );
  // Post-clean: remove awkward artefacts when a placeholder was empty.
  //   "Please find attached Quote QT-1 for ."       → "Please find attached Quote QT-1."
  //   "Please find attached Quote QT-1 for ,"       → "Please find attached Quote QT-1,"
  //   "…attached Quote QT-1 for \n"                 → "…attached Quote QT-1\n"
  // We only strip when the "for " is immediately followed by punctuation
  // or line-break so we never mangle legitimate wording. The leading
  // whitespace before "for" is also consumed so we don't leave a stray
  // space before the punctuation ("Quote QT-1 ." → "Quote QT-1.").
  //
  // EVENTSFLO-QUOTE-INVOICE-EMAIL-TEMPLATE-SETTINGS-VISIBILITY-AND-PORTAL-COPY-FIX-V1
  // Also collapse triple+ blank lines (`\n\n\n+`) down to a single
  // paragraph break — an empty `{{client_portal_link}}` on its own
  // line otherwise leaves an awkward extra gap in the rendered email.
  return substituted
    .replace(/\s*\bfor\s+([.,;:!?])/g, "$1")
    .replace(/\s*\bfor\s*(?=\r?\n)/g, "")
    .replace(/\s*\bfor\s+$/g, "")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/\n{3,}/g, "\n\n");
};

export function EmailComposeModal({
  open,
  onClose,
  documentType, // "quote", "invoice", or "estimate"
  documentId,
  documentNumber,
  documentData, // { total, dueDate, validUntil, balanceDue }
  clientId, // NEW: Client ID for loading contacts
  clientEmail, // Fallback if no contacts
  clientName,
  companyName,
  currencySymbol = "£",
  paymentTerms,
  projectName, // EVENTSFLO-QUOTE-INVOICE-EMAIL-BODY-TEMPLATES-SETTINGS-V1
  onSend,
}) {
  const [to, setTo] = useState([]);
  const [toInput, setToInput] = useState("");
  const [cc, setCc] = useState([]);
  const [ccInput, setCcInput] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [templateId, setTemplateId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(true);
  
  // Client contacts state
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [noFlaggedWarning, setNoFlaggedWarning] = useState(false);
  // EVENTSFLO-QUOTE-INVOICE-EMAIL-TEMPLATE-SETTINGS-VISIBILITY-AND-PORTAL-COPY-FIX-V1
  const [clientPortalLink, setClientPortalLink] = useState("");

  // Template variables
  // EVENTSFLO-QUOTE-INVOICE-EMAIL-BODY-TEMPLATES-SETTINGS-V1
  // `project_name` and `document_type` added. Missing project name
  // resolves to an empty string so the "for " artefact is stripped by
  // `replaceVariables`.
  const _docTypeLabel =
    documentType === "quote"
      ? "Quote"
      : documentType === "invoice"
      ? "Invoice"
      : documentType === "estimate"
      ? "Estimate"
      : documentType || "";
  const templateVariables = {
    company_name: companyName || "Your Company",
    client_name: clientName || "Valued Client",
    document_number: documentNumber || "",
    document_type: _docTypeLabel,
    project_name: (projectName || "").trim(),
    total_amount: documentData?.total?.toFixed(2) || "0.00",
    due_date: documentData?.dueDate || "",
    valid_until: documentData?.validUntil || "",
    currency_symbol: currencySymbol,
    currency: currencySymbol,
    payment_terms: paymentTerms || "Net 30",
    balance_due: documentData?.balanceDue?.toFixed(2) || documentData?.total?.toFixed(2) || "0.00",
    // Also expose the legacy per-type keys so custom templates keep
    // working if the operator wrote `{{quote_number}}` / `{{invoice_number}}`.
    quote_number: documentType === "quote" ? documentNumber || "" : "",
    invoice_number: documentType === "invoice" ? documentNumber || "" : "",
    // EVENTSFLO-QUOTE-INVOICE-EMAIL-TEMPLATE-SETTINGS-VISIBILITY-AND-PORTAL-COPY-FIX-V1
    // Resolved absolute URL to the Client Portal (quotes list /
    // invoices list). Empty string when the resolver cannot compute
    // one — the smart-strip below removes the surrounding line so
    // the client never sees a broken/blank link.
    client_portal_link: clientPortalLink || "",
  };

  // Load default template when modal opens
  useEffect(() => {
    if (open) {
      loadDefaultTemplate();
      loadClientContacts();
      loadClientPortalLink();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, documentType, clientId]);

  // EVENTSFLO-QUOTE-INVOICE-EMAIL-TEMPLATE-SETTINGS-VISIBILITY-AND-PORTAL-COPY-FIX-V1
  const loadClientPortalLink = async () => {
    try {
      const res = await getClientPortalUrl(documentType);
      setClientPortalLink((res.data && res.data.url) || "");
    } catch (err) {
      // Non-fatal — the smart-strip handles an empty link cleanly.
      setClientPortalLink("");
    }
  };

  // Load client contacts and auto-populate recipients
  const loadClientContacts = async () => {
    if (!clientId) {
      // Fallback to clientEmail if no clientId
      if (clientEmail && to.length === 0) {
        setTo([clientEmail]);
      }
      return;
    }

    setContactsLoading(true);
    try {
      const res = await getClientContacts(clientId, { active_only: true });
      const allContacts = res.data || [];
      setContacts(allContacts);

      // Determine which flag to check based on document type
      const flagField = documentType === "estimate" ? "in_estimate"
        : documentType === "quote" ? "in_quote"
        : "in_invoice";

      // EVENTSFLO-INVOICE-SEND-RECIPIENT-CONFIRMATION-USING-SHARED-MODAL-V1
      // For invoices we ALSO pre-check contacts whose role clearly
      // marks them as billing/accounts/finance/invoice contacts. The
      // union of `in_invoice=true` + role-match forms the pre-checked
      // set. All contacts remain visible (via the "Add from Client
      // Contacts" collapsible) so the user can deselect / add manually.
      const isBillingRole = (role) => {
        if (!role) return false;
        const r = String(role).toLowerCase();
        return (
          r.includes("billing") ||
          r.includes("accounts") ||
          r.includes("finance") ||
          r.includes("invoice")
        );
      };

      const flaggedByField = allContacts.filter(c => c[flagField]);
      const flaggedByRole = documentType === "invoice"
        ? allContacts.filter(c => !c[flagField] && isBillingRole(c.role))
        : [];
      const flaggedContacts = [...flaggedByField, ...flaggedByRole];

      if (flaggedContacts.length > 0) {
        // Pre-populate To field with flagged contacts (deduplicated by email)
        const seen = new Set();
        const emails = [];
        for (const c of flaggedContacts) {
          const key = (c.email || "").toLowerCase();
          if (!key || seen.has(key)) continue;
          seen.add(key);
          emails.push(c.email);
        }
        setTo(emails);
        setNoFlaggedWarning(false);
      } else if (allContacts.length > 0) {
        // No contacts flagged for this document type - show warning
        setNoFlaggedWarning(true);
        // Suggest primary contact as fallback
        const primary = allContacts.find(c => c.is_primary);
        if (primary) {
          setTo([primary.email]);
        }
      } else if (clientEmail) {
        // No contacts at all - use client email
        setTo([clientEmail]);
      }
    } catch (err) {
      console.error("Failed to load contacts:", err);
      // Fallback to client email
      if (clientEmail && to.length === 0) {
        setTo([clientEmail]);
      }
    } finally {
      setContactsLoading(false);
    }
  };

  const loadDefaultTemplate = async () => {
    setLoadingTemplate(true);
    try {
      const res = await getDefaultEmailTemplate(documentType);
      const template = res.data;
      
      if (template) {
        setTemplateId(template.id);
        setSubject(replaceVariables(template.subject, templateVariables));
        setBody(replaceVariables(template.body, templateVariables));
      } else {
        // EVENTSFLO-QUOTE-INVOICE-EMAIL-TEMPLATE-SETTINGS-VISIBILITY-AND-PORTAL-COPY-FIX-V1
        // Portal-led fallback used when no DB template exists yet.
        // Mirrors the backend platform default. Empty {{project_name}}
        // or {{client_portal_link}} is scrubbed by the smart-strip.
        const defaultSubject =
          documentType === "quote"
            ? `Quote ${documentNumber} from ${companyName || "Your Company"}`
            : `Invoice ${documentNumber} from ${companyName || "Your Company"}`;

        const defaultBodyTemplate =
          documentType === "quote"
            ? "Hello {{client_name}},\n\nYour quote for {{project_name}} is ready to review in the Client Portal:\n\n{{client_portal_link}}\n\nA PDF copy of Quote {{document_number}} is also attached for convenience.\n\nPlease review the details and let us know if you have any questions or would like us to make any changes.\n\nKind regards,\n{{company_name}}"
            : "Hello {{client_name}},\n\nYour invoice for {{project_name}} is available in the Client Portal:\n\n{{client_portal_link}}\n\nA PDF copy of Invoice {{document_number}} is also attached for convenience.\n\nPlease let us know if you have any questions.\n\nKind regards,\n{{company_name}}";

        setSubject(defaultSubject);
        setBody(replaceVariables(defaultBodyTemplate, templateVariables));
      }
    } catch (err) {
      console.error("Failed to load template:", extractErrorMessage(err));
      // Use fallback defaults
      setSubject(`${documentType === "quote" ? "Quote" : "Invoice"} ${documentNumber}`);
      setBody(`Please find attached ${documentType} ${documentNumber}.`);
    } finally {
      setLoadingTemplate(false);
    }
  };

  const addEmail = (type, email) => {
    // EVENTSFLO-QUOTE-INVOICE-SEND-RECIPIENT-CONFIRMATION-V1
    // Parse the entry with the reusable parser. Supports
    //   plain@x.com
    //   Name <plain@x.com>
    //   Name plain@x.com
    //   Name (plain@x.com)
    //   plus comma-separated multiples.
    const parsed = parseRecipients(email);
    const validEmails = [];
    const invalid = [];
    for (const p of parsed) {
      if (p.valid && p.email) validEmails.push(p.email.toLowerCase());
      else invalid.push(p.raw || p.email || "");
    }
    if (invalid.length > 0) {
      toast.error(`Invalid email address: ${invalid.join(", ")}`);
      return;
    }
    if (validEmails.length === 0) return;

    if (type === "to") {
      const merged = [...to];
      for (const e of validEmails) if (!merged.includes(e)) merged.push(e);
      setTo(merged);
      setToInput("");
    } else {
      const merged = [...cc];
      for (const e of validEmails) if (!merged.includes(e)) merged.push(e);
      setCc(merged);
      setCcInput("");
    }
  };

  const removeEmail = (type, email) => {
    if (type === "to") {
      setTo(to.filter((e) => e !== email));
    } else {
      setCc(cc.filter((e) => e !== email));
    }
  };

  const handleKeyDown = (type, e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addEmail(type, type === "to" ? toInput : ccInput);
    }
  };

  const handleSend = async () => {
    if (to.length === 0) {
      toast.error("Please add at least one recipient");
      return;
    }
    
    if (!subject.trim()) {
      toast.error("Subject is required");
      return;
    }
    
    if (!body.trim()) {
      toast.error("Email body is required");
      return;
    }
    
    setLoading(true);
    try {
      await onSend({
        to,
        cc,
        subject,
        body,
        template_id: templateId,
      });
      toast.success(`Email sent to ${to.join(", ")}`);
      handleClose();
    } catch (err) {
      toast.error(`Failed to send email: ${extractErrorMessage(err)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTo([]);
    setToInput("");
    setCc([]);
    setCcInput("");
    setSubject("");
    setBody("");
    setTemplateId(null);
    setContacts([]);
    setNoFlaggedWarning(false);
    onClose();
  };

  // Add contact to To or CC
  const addContactToRecipients = (contact, field = "to") => {
    if (field === "to") {
      if (!to.includes(contact.email)) {
        setTo([...to, contact.email]);
      }
    } else {
      if (!cc.includes(contact.email)) {
        setCc([...cc, contact.email]);
      }
    }
  };

  // Get document type label for UI
  const getDocTypeLabel = () => {
    switch (documentType) {
      case "estimate": return "Estimate";
      case "quote": return "Quote";
      case "invoice": return "Invoice";
      default: return documentType;
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl bg-surface-base border-border text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Mail className="h-5 w-5 text-teal-400" />
            Send {getDocTypeLabel()} via Email
          </DialogTitle>
        </DialogHeader>

        {loadingTemplate || contactsLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-teal-400" />
          </div>
        ) : (
          <div className="space-y-4" data-testid="email-compose-form">
            {/* Document Info Banner */}
            <div className="flex items-center gap-2 p-3 bg-surface-elevated rounded-lg border border-border">
              <FileText className="h-4 w-4 text-teal-400" />
              <span className="text-sm text-foreground">
                {getDocTypeLabel()}: 
                <span className="font-medium text-white ml-1">{documentNumber}</span>
              </span>
              <span className="text-muted-foreground mx-2">•</span>
              <span className="text-sm text-foreground">
                PDF will be attached automatically
              </span>
            </div>

            {/* Warning: No contacts flagged for this document type */}
            {noFlaggedWarning && (
              <div className="flex items-start gap-3 p-3 bg-amber-900/30 border border-amber-600/50 rounded-lg" data-testid="no-contacts-warning">
                <AlertTriangle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-300">
                    No contacts flagged for {getDocTypeLabel()}s
                  </p>
                  <p className="text-xs text-amber-400/80 mt-1">
                    Configure contacts in Client Details to auto-populate recipients. Using primary contact as fallback.
                  </p>
                </div>
              </div>
            )}

            {/* EVENTSFLO-QUOTE-INVOICE-SEND-RECIPIENT-CONTACTS-REGRESSION-FIX-V1
                Client Contacts checklist — default-expanded, checkbox
                per contact (select/deselect straight from the section),
                empty-state message when the client has no contacts. */}
            <div
              className="space-y-2"
              data-testid="email-compose-client-contacts-section"
            >
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <Label className="text-foreground">
                  Client Contacts {contacts.length > 0 && `(${contacts.length})`}
                </Label>
              </div>
              {contacts.length > 0 ? (
                <div className="space-y-1 p-2 bg-surface-elevated/30 rounded-lg border border-border max-h-56 overflow-y-auto">
                  {contacts.map((contact) => {
                    const email = (contact.email || "").trim();
                    const hasEmail = !!email;
                    const checked = hasEmail && to.some(
                      (e) => e.toLowerCase() === email.toLowerCase()
                    );
                    const rowId = `contact-row-${contact.id}`;
                    return (
                      <label
                        key={contact.id}
                        htmlFor={rowId}
                        className={`flex items-center gap-3 p-2 rounded text-sm ${
                          hasEmail
                            ? "hover:bg-surface-elevated/50 cursor-pointer"
                            : "opacity-50 cursor-not-allowed"
                        }`}
                        title={
                          hasEmail
                            ? "Select to include this contact as a recipient"
                            : "This contact has no valid email address"
                        }
                      >
                        <Checkbox
                          id={rowId}
                          checked={checked}
                          disabled={!hasEmail}
                          onCheckedChange={(next) => {
                            if (!hasEmail) return;
                            if (next) {
                              // Add if not already present (case-insensitive).
                              if (
                                !to.some(
                                  (e) => e.toLowerCase() === email.toLowerCase()
                                )
                              ) {
                                setTo([...to, email]);
                              }
                            } else {
                              // Remove exact email regardless of case.
                              setTo(
                                to.filter(
                                  (e) => e.toLowerCase() !== email.toLowerCase()
                                )
                              );
                            }
                          }}
                          data-testid="email-compose-client-contact-checkbox"
                          data-contact-email={email}
                        />
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          {contact.is_primary && (
                            <Star className="h-3 w-3 text-amber-400 flex-shrink-0" />
                          )}
                          <span className="text-foreground truncate">
                            {contact.first_name} {contact.last_name}
                          </span>
                          {contact.role && (
                            <span className="text-xs text-muted-foreground">
                              ({contact.role})
                            </span>
                          )}
                          <span className="text-xs text-muted-foreground truncate">
                            {hasEmail ? email : "(no email)"}
                          </span>
                        </div>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <p
                  className="text-xs text-muted-foreground p-2 border border-dashed border-border rounded-md"
                  data-testid="email-compose-client-contacts-empty"
                >
                  No client contacts with email addresses found. Add a manual
                  recipient below.
                </p>
              )}
            </div>

            {/* To Field */}
            <div className="space-y-2">
              <Label className="text-foreground">To *</Label>
              <div className="flex flex-wrap gap-2 p-2 min-h-[42px] bg-surface-elevated border border-border rounded-md focus-within:ring-2 focus-within:ring-teal-500">
                {to.map((email) => (
                  <Badge
                    key={email}
                    variant="secondary"
                    className="bg-surface-elevated text-foreground gap-1"
                  >
                    {email}
                    <X
                      className="h-3 w-3 cursor-pointer hover:text-red-400"
                      onClick={() => removeEmail("to", email)}
                    />
                  </Badge>
                ))}
                <Input
                  type="email"
                  value={toInput}
                  onChange={(e) => setToInput(e.target.value)}
                  onKeyDown={(e) => handleKeyDown("to", e)}
                  onBlur={() => toInput && addEmail("to", toInput)}
                  placeholder={to.length === 0 ? "Enter email addresses..." : ""}
                  className="flex-1 min-w-[200px] border-0 bg-transparent focus-visible:ring-0 p-0 h-auto text-white placeholder:text-muted-foreground"
                  data-testid="email-compose-manual-email-input"
                />
              </div>
              <p className="text-xs text-muted-foreground">Press Enter or comma to add multiple recipients</p>
            </div>

            {/* CC Field */}
            <div className="space-y-2">
              <Label className="text-foreground">CC (Optional)</Label>
              <div className="flex flex-wrap gap-2 p-2 min-h-[42px] bg-surface-elevated border border-border rounded-md focus-within:ring-2 focus-within:ring-teal-500">
                {cc.map((email) => (
                  <Badge
                    key={email}
                    variant="secondary"
                    className="bg-surface-elevated text-foreground gap-1"
                  >
                    {email}
                    <X
                      className="h-3 w-3 cursor-pointer hover:text-red-400"
                      onClick={() => removeEmail("cc", email)}
                    />
                  </Badge>
                ))}
                <Input
                  type="email"
                  value={ccInput}
                  onChange={(e) => setCcInput(e.target.value)}
                  onKeyDown={(e) => handleKeyDown("cc", e)}
                  onBlur={() => ccInput && addEmail("cc", ccInput)}
                  placeholder={cc.length === 0 ? "Enter CC addresses..." : ""}
                  className="flex-1 min-w-[200px] border-0 bg-transparent focus-visible:ring-0 p-0 h-auto text-white placeholder:text-muted-foreground"
                  data-testid="email-cc-input"
                />
              </div>
            </div>

            {/* Subject */}
            <div className="space-y-2">
              <Label className="text-foreground">Subject *</Label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Email subject"
                className="bg-surface-elevated border-border text-white"
                data-testid="email-subject-input"
              />
            </div>

            {/* Body */}
            <div className="space-y-2">
              <Label className="text-foreground">Message *</Label>
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Email message"
                rows={8}
                className="bg-surface-elevated border-border text-white resize-none"
                data-testid="email-body-input"
              />
            </div>
          </div>
        )}

        {/* EVENTSFLO-QUOTE-INVOICE-SEND-RECIPIENT-CONFIRMATION-V1
            Final recipients preview — always visible so the operator
            sees exactly who will receive the send before confirming.
            Send button is disabled below whenever this list is empty
            or any recipient is not a syntactically-valid email. */}
        <div
          className="px-6 py-3 border-t bg-muted/30"
          data-testid="final-recipients-summary"
        >
          <div
            className="text-xs font-medium text-muted-foreground mb-1"
            data-testid="email-compose-final-recipients"
          >
            Final recipients — this email will be sent to:
          </div>
          {to.length === 0 ? (
            <div
              className="text-xs text-red-500"
              data-testid="final-recipients-empty"
            >
              At least one recipient is required.
            </div>
          ) : (
            <ul className="flex flex-wrap gap-1.5 text-xs">
              {to.map((addr) => {
                const ok = isValidEmail(addr);
                return (
                  <li
                    key={`final-${addr}`}
                    className={
                      "rounded-full px-2 py-0.5 border font-mono " +
                      (ok
                        ? "bg-teal-500/10 border-teal-500/40 text-teal-500"
                        : "bg-red-500/10 border-red-500/50 text-red-500")
                    }
                    data-testid={`final-recipient-${addr}`}
                  >
                    {ok ? addr : `Invalid email address: ${addr}`}
                  </li>
                );
              })}
            </ul>
          )}
          {cc.length > 0 && (
            <div className="text-xs text-muted-foreground mt-2">
              Cc: {cc.join(", ")}
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            className="border-border text-foreground hover:bg-surface-elevated"
            disabled={loading}
          >
            Cancel
          </Button>
          <span data-testid="email-compose-send-button">
          <Button
            onClick={handleSend}
            disabled={
              loading ||
              loadingTemplate ||
              to.length === 0 ||
              to.some((e) => !isValidEmail(e))
            }
            className="bg-teal-600 hover:bg-teal-700 text-white"
            data-testid="send-email-submit-btn"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Mail className="h-4 w-4 mr-2" />
                Send Email
              </>
            )}
          </Button>
          </span>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default EmailComposeModal;
